import cx from 'classnames';
import * as React from 'react';

import Button from 'components/ButtonDeprecated';
import CurrentUser from 'auth/CurrentUser';
import DeprecatedTokenizerTypeahead from 'components/DeprecatedTokenizerTypeahead';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageStarterItem from './MessageStarterItem';
// @ts-ignore
import RecipientToken from 'chat_v2/components/ComposerRecipientToken';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import apiRequest from 'lib/apiRequest';
import RemindAPI from 'lib/RemindAPI';
import COMPOSER_RECIPIENTS_QUERY from 'chat_v2/components/SplitComposer/queries/ComposerRecipientsQuery.gql';
import { ComposerRecipients, ComposerRecipientsVariables, ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity } from 'chat_v2/components/SplitComposer/queries/__generated__/ComposerRecipients';
import { ComposerMessageType } from 'globalTypes';
import IndividualRecipientAvatar from 'chat_v2/components/IndividualRecipientAvatar';

import './styles/GroupMessageStarter.scss';

type Props = {
  additionalChatMembersLimit: number,
  onRequestHide?: Function,
  onCreateRemoteChat?: Function,
  className?: string;
};

type State = {
  selectedMembers: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity[],
};

export default class GroupMessageStarter extends React.Component<Props, State> {
  state: State = {
    selectedMembers: [],
  };

  componentDidMount() {
    this.fetchData('');
  }

  fetchData(query: string) {
    return RemindAPI.execute<ComposerRecipients, ComposerRecipientsVariables>({ query: COMPOSER_RECIPIENTS_QUERY, variables: { type: ComposerMessageType.conversation, query: query } }).then(data => {
      const peopleHeaderIndex = data.composerRecipients.recipients.findIndex(entity => entity.__typename === 'TextHeader' && entity.text === 'People');
      return data.composerRecipients.recipients.splice(peopleHeaderIndex + 1, data.composerRecipients.recipients.length - 1);
    });
  }

  handleSelectionChange = (selectedMembers: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity[]) => {
    this.setState({ selectedMembers });
  };

  startConversation = () => {
    const memberUuids = this.state.selectedMembers.map(entity => entity.recipient.uuid);
    memberUuids.push(CurrentUser.getUserUuid());

    apiRequest('/chats', 'POST', {
      chat: { member_uuids: memberUuids },
    })
    .then(remoteChat => {
      if (this.props.onCreateRemoteChat) {
        this.props.onCreateRemoteChat(remoteChat);
      }
      if (this.props.onRequestHide) {
        this.props.onRequestHide();
      }
    }).catch(error => {
      ToastActions.showErrorToast(error.error);
      if (this.props.onRequestHide) {
        this.props.onRequestHide();
      }
    });
  };

  isAtChatMembersLimit = () => {
    const { selectedMembers } = this.state;
    return selectedMembers.length === this.props.additionalChatMembersLimit;
  };

  isOverChatMembersLimit = () => {
    const { selectedMembers } = this.state;
    return selectedMembers.length > this.props.additionalChatMembersLimit;
  };

  renderActionButton = () => {
    return (
      <Button disabled={this.state.selectedMembers.length === 0} onClick={this.startConversation}>
        Go
      </Button>
    );
  };

  renderInputToken(entity: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity) {
    return (
      <RecipientToken>
        <IndividualRecipientAvatar entity={entity} size="xsmall" />
        {entity.content.title}
      </RecipientToken>
    );
  }

  renderParticipantRemainingCount = () => {
    const { selectedMembers } = this.state;
    if (selectedMembers.length === 0) {
      return null;
    }

    const { additionalChatMembersLimit } = this.props;
    const remainingCount = additionalChatMembersLimit - selectedMembers.length;
    let text = `You can add ${remainingCount} more people`;
    if (this.isOverChatMembersLimit()) {
      text = 'You have exceeded the maximum number of participants';
    } else if (this.isAtChatMembersLimit()) {
      text = 'You have reached the maximum number of participants';
    } else if (remainingCount === 1) {
      text = 'You can add 1 more person';
    }

    return (
      <Text color={this.isOverChatMembersLimit() ? 'apple' : 'percussion'} size="xxsmall">
        {text}
      </Text>
    );
  };

  renderMember = (
    entity: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity,
    highlighted: boolean,
    query: string,
    onSelect: Function
  ) => {
    return (
      <li onClick={() => onSelect()} key={entity.recipient.uuid}>
        <MessageStarterItem
          className={cx('GroupMessageStarter-results-item', {
            'GroupMessageStarter-results-item--highlight': highlighted,
          })}
          entity={entity}
          query={query}
          selectable={this.state.selectedMembers.length > 0}
          selected={this.state.selectedMembers.includes(entity)}
        />
      </li>
    );
  };

  renderList = (items: Array<any>, loading: boolean) => {
    return (
      <div>
        {this.renderParticipantRemainingCount()}
        <div className="GroupMessageStarter-resultsWrap">
          <ul className="GroupMessageStarter-results">{items}</ul>
          {loading && (
            <div className="GroupMessageStarter-loadingIndicator">
              <LoadingIndicator />
            </div>
          )}
        </div>
      </div>
    );
  };

  renderNoResults(query: string) {
    return (
      <li key="noResults">
        <Text color="chalkboard" size="xsmall" weight="semibold">
          No people found for {`"${query}"`}
        </Text>
      </li>
    );
  }

  render() {
    const actionButton = (
      <Button
        data-test-selector="start_group_chat_button"
        disabled={this.state.selectedMembers.length === 0 || this.isOverChatMembersLimit()}
        onClick={this.startConversation}>
        Go
      </Button>
    );

    return (
      <div className={cx('GroupMessageStarter', this.props.className)}>
        <DeprecatedTokenizerTypeahead
          prompt="To:"
          placeholder="Select or search for people"
          // @ts-ignore
          value={this.state.selectedMembers || []}
          onChange={this.handleSelectionChange}
          dataSource={this.fetchData}
          dataFilterFactory={() => () => true}
          listRenderer={this.renderList}
          itemRenderer={this.renderMember}
          tokenRenderer={this.renderInputToken}
          noResultsRenderer={this.renderNoResults}
          actionButton={actionButton}
          autoFocus
          inline
        />
      </div>
    );
  }
}
